import { useEffect } from "react";
import LazyMap from "@/components/site/LazyMap";
import { Link } from "@/lib/router-compat";
import {
  MapPin,
  Check,
  Shield,
  Phone,
  Lock,
  Car,
  Bus,
  Monitor,
  Calendar,
  Clock,
  Pill,
  Network,
} from "lucide-react";
import Header from "@/components/site/Header";
import SEOHead from "@/components/SEOHead";
import Footer from "@/components/site/Footer";
import FloatingCTAs from "@/components/site/FloatingCTAs";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import InsurancePills from "@/components/site/InsurancePills";
import RelatedLinks from "@/components/site/RelatedLinks";
import LocalContextSection, { LocalLink } from "@/components/site/LocalContextSection";
import LocalCoverageSection from "@/components/site/LocalCoverageSection";

const PHONE = "856-200-3127";
const TEL = "tel:8562003127";
const CANONICAL =
  "https://cherryhillrecoverycenter.com/addiction-treatment-camden-nj";
const TITLE =
  "Addiction Treatment Near Camden, NJ | Cherry Hill Recovery Center";
const DESCRIPTION =
  "Addiction treatment near Camden, NJ — PHP, IOP, MAT & detox referral at Cherry Hill Recovery Center. Most insurance accepted. Call 856-200-3127.";

const SERIF = "Georgia, 'Times New Roman', serif";
const SANS = "Helvetica, Arial, sans-serif";

type PillItem = { label: string; href: string };

const programs: PillItem[] = [
  { label: "Virtual IOP", href: "/virtual-iop-new-jersey" },
  { label: "Psychiatric Evaluation", href: "/psychiatric-evaluation-nj" },
  { label: "Drug & Alcohol Evaluation", href: "/drug-alcohol-evaluation-nj" },
];

const conditions: PillItem[] = [
  { label: "Heroin & Opioid Addiction", href: "/heroin-opioid-addiction-treatment-nj" },
  { label: "Alcohol Addiction", href: "/alcohol-addiction-treatment-cherry-hill-nj" },
  { label: "Fentanyl Addiction", href: "/fentanyl-addiction-treatment-nj" },
  { label: "Prescription Painkillers", href: "/painkiller-addiction-treatment-nj" },
  { label: "Benzodiazepine Addiction", href: "/benzodiazepine-addiction-treatment-nj" },
  { label: "Cocaine Addiction", href: "/cocaine-addiction-treatment-nj" },
  { label: "Marijuana Addiction", href: "/marijuana-addiction-treatment-nj" },
  { label: "Adderall & Stimulants", href: "/adderall-addiction-treatment-nj" },
  { label: "Methadone", href: "/methadone-addiction-treatment-nj" },
  { label: "Synthetic Drugs (K2, Zaza)", href: "/synthetic-drug-treatment-nj" },
  { label: "Dual Diagnosis", href: "/psychiatric-evaluation-nj" },
];

const insurers: string[] = [
  "Aetna",
  "Anthem",
  "Blue Cross Blue Shield",
  "Cigna",
  "United Healthcare",
  "Humana",
  "Magellan",
  "Beacon",
  "Compsych",
  "Health Net",
  "Independence Administrators",
  "Carefirst",
  "& More",
];

const nearby: PillItem[] = [
  { label: "Cherry Hill", href: "/" },
  { label: "Voorhees", href: "/addiction-treatment-voorhees-nj" },
  { label: "Pennsauken", href: "/addiction-treatment-pennsauken-nj" },
  { label: "Haddonfield", href: "/addiction-treatment-haddonfield-nj" },
  { label: "Moorestown", href: "/addiction-treatment-moorestown-nj" },
  { label: "Marlton", href: "/addiction-treatment-marlton-nj" },
  { label: "Camden County", href: "/addiction-treatment-camden-county-nj" },
];

const faqs = [
  {
    q: "How far is Cherry Hill Recovery Center from Camden?",
    a: "Cherry Hill Recovery Center is approximately 5 miles and 10 minutes from Camden City — accessible via Route 38 West or Haddonfield Road to Marlton Pike East in Cherry Hill. Public transit options are also available for Camden residents without transportation. Virtual IOP is available for patients who prefer to attend from home.",
  },
  {
    q: "What insurance do you accept for Camden residents?",
    a: "Cherry Hill Recovery Center accepts most major private insurance plans — including employer-sponsored and marketplace plans. Substance use disorder treatment including PHP, IOP, and MAT is covered under New Jersey's mental health parity requirements. Our admissions team verifies your specific coverage before treatment begins at no cost. Call 856-200-3127 to start.",
  },
  {
    q: "Can I attend treatment while still working in Camden?",
    a: "Yes. Our Intensive Outpatient Program runs three to four evenings per week starting between 5pm and 6pm — so patients from Camden can complete a full workday before attending. The commute from Camden is short and direct, and Virtual IOP is also available for patients who prefer to attend from home.",
  },
  {
    q: "What is the difference between PHP and IOP?",
    a: "PHP — Partial Hospitalization Program — provides five to six days per week of intensive treatment, typically five to eight hours per day. IOP — Intensive Outpatient Program — provides three to four days per week, approximately three hours per session, allowing patients to maintain work and family responsibilities. The right level is determined by a free clinical assessment our team conducts before treatment begins.",
  },
  {
    q: "Do you provide detox services?",
    a: "Cherry Hill Recovery Center does not operate an on-site detox facility. We assess each patient's needs and refer them to the most appropriate medically supervised detox provider — coordinating the entire process and transitioning patients directly into our PHP or IOP program upon completion. most major insurance plans cover this referral and the program that follows.",
  },
  {
    q: "Are there addiction resources for Camden residents?",
    a: "Cherry Hill Recovery Center serves Camden residents with the full spectrum of outpatient addiction care — PHP, IOP, MAT, psychiatric evaluation, and detox referral — and accepts most major private insurance plans. For additional NJ-wide resources, SAMHSA's helpline is available 24/7 at 1-800-662-4357, and NJ Mental Health Cares can be reached at 1-866-202-4357. Our admissions team is also available around the clock at 856-200-3127.",
  },
  {
    q: "Where is the closest drug rehab to Camden, NJ?",
    a: "Cherry Hill Recovery Center is one of the closest outpatient rehab programs to Camden City — about 5 miles east on Route 38, roughly a 10-minute drive. We serve every Camden neighborhood, including Fairview, Parkside, Cramer Hill, Waterfront South, East Camden, and Centerville, plus nearby Pennsauken, Collingswood, and Gloucester City. Call 856-200-3127 for a free assessment.",
  },
  {
    q: "Do you offer Suboxone or methadone treatment for Camden patients?",
    a: "We provide medication-assisted treatment with buprenorphine (Suboxone), Sublocade, and Vivitrol for Camden patients with opioid use disorder. MAT is paired with counseling in PHP or IOP rather than offered on its own. We do not dispense methadone, but we can coordinate a referral to a licensed opioid treatment program if methadone is the better clinical fit for you.",
  },
  {
    q: "Can I get same-day help if I live in Camden?",
    a: "Often, yes. Our admissions line answers 24/7 and most Camden callers complete a free clinical assessment the same day they call. If you need medically supervised detox first, we arrange the placement and coordinate the handoff so you move straight into PHP or IOP when detox is done. Call 856-200-3127 to start.",
  },
  {
    q: "Do I need a car to get to treatment from Camden?",
    a: "No. NJ Transit bus routes connect Camden to Cherry Hill, and many patients rideshare for the short trip. Our Virtual IOP is also a full option for Camden residents who cannot travel — same clinicians, same curriculum, attended by secure video from home.",
  },
];


const PillLink = ({ to, children }: { to: string; children: React.ReactNode }) => (
  <Link
    to={to}
    style={{
      display: "inline-flex",
      alignItems: "center",
      background: "#FFFFFF",
      border: "1px solid #F0E0E0",
      borderRadius: 50,
      padding: "10px 20px",
      color: "#8B1A1A",
      fontWeight: 700,
      fontSize: 13,
      margin: 5,
      textDecoration: "none",
      fontFamily: SANS,
    }}
  >
    {children}
  </Link>
);

const SidebarLink = ({ to, children }: { to: string; children: React.ReactNode }) => (
  <Link
    to={to}
    style={{
      display: "block",
      color: "#8B1A1A",
      fontSize: 14,
      textDecoration: "none",
      padding: "6px 0",
    }}
  >
    {children}
  </Link>
);

const AddictionTreatmentCamdenNJ = () => {
  
  const clinicSchema = {
    "@context": "https://schema.org",
    "@type": "MedicalClinic",
    name: "Cherry Hill Recovery Center",
    description:
      "Addiction treatment serving Camden NJ — PHP, IOP, MAT and detox referral",
    url: CANONICAL,
    telephone: "856-200-3127",
    address: {
      "@type": "PostalAddress",
      streetAddress: "1930 Marlton Pike East",
      addressLocality: "Cherry Hill",
      addressRegion: "NJ",
      postalCode: "08003",
    },
    areaServed: {
      "@type": "City",
      name: "Camden, New Jersey",
    },
  };

  const faqSchema = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqs.map((f) => ({
      "@type": "Question",
      name: f.q,
      acceptedAnswer: { "@type": "Answer", text: f.a },
    })),
  };

  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: "https://cherryhillrecoverycenter.com/" },
      { "@type": "ListItem", position: 2, name: "Addiction Treatment", item: "https://cherryhillrecoverycenter.com/" },
      { "@type": "ListItem", position: 3, name: "Camden, NJ", item: CANONICAL },
    ],
  };

  return (
    <div className="min-h-screen flex flex-col bg-white">
      <SEOHead title={TITLE} description={DESCRIPTION} canonical={CANONICAL} schemaJson={[clinicSchema, faqSchema, breadcrumbSchema]} />
      <Header />

      {/* Breadcrumb */}
      <nav aria-label="Breadcrumb" style={{ background: "#FAF4F0", padding: "12px 0" }}>
        <div style={{ maxWidth: 1100, margin: "0 auto", padding: "0 20px", fontSize: 13, color: "#2C2C2C", fontFamily: SANS }}>
          <Link to="/" style={{ color: "#8B1A1A", textDecoration: "none" }}>Home</Link>
          <span style={{ margin: "0 8px", color: "#999" }}>›</span>
          <Link to="/" style={{ color: "#8B1A1A", textDecoration: "none" }}>Addiction Treatment</Link>
          <span style={{ margin: "0 8px", color: "#999" }}>›</span>
          <span>Camden, NJ</span>
        </div>
      </nav>

      {/* HERO */}
      <header
        style={{
          background:
            "linear-gradient(90deg, #6B1414 0%, #8B1A1A 100%), radial-gradient(circle at 20% 30%, rgba(250,244,240,0.06) 1px, transparent 1px)",
          backgroundSize: "auto, 24px 24px",
          padding: "96px 20px",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <div style={{ maxWidth: 760, margin: "0 auto", textAlign: "center", width: "100%" }}>
          <p
            style={{
              color: "#FAF4F0",
              fontSize: 11,
              textTransform: "uppercase",
              letterSpacing: "0.15em",
              marginBottom: 12,
              fontFamily: SANS,
              fontWeight: 600,
            }}
          >
            Serving Camden, NJ and Surrounding Communities
          </p>
          <h1
            style={{
              fontFamily: SERIF,
              fontSize: 40,
              color: "#FFFFFF",
              lineHeight: 1.15,
              margin: 0,
            }}
          >
            Addiction Treatment Near <span style={{ color: "#FAC8C8" }}>Camden, NJ</span>
          </h1>
          <p
            style={{
              color: "#FAF4F0",
              fontSize: 17,
              margin: "12px auto 24px",
              maxWidth: 600,
              lineHeight: 1.55,
              fontFamily: SANS,
            }}
          >
            PHP, IOP, MAT, and detox referral from Cherry Hill Recovery Center — accessible from Camden, most insurance accepted, available now.
          </p>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 12, justifyContent: "center" }}>
            <a
              href={TEL}
              aria-label={`Call Cherry Hill Recovery Center at ${PHONE}`}
              style={{
                background: "#FAF4F0",
                color: "#8B1A1A",
                padding: "12px 26px",
                borderRadius: 999,
                fontWeight: 700,
                textDecoration: "none",
                fontSize: 15,
                fontFamily: SANS,
              }}
            >
              Call {PHONE}
            </a>
            <Link
              to="/verify-insurance"
              aria-label="Verify your insurance coverage"
              style={{
                background: "transparent",
                color: "#FAF4F0",
                border: "2px solid #FAF4F0",
                padding: "10px 24px",
                borderRadius: 999,
                fontWeight: 700,
                textDecoration: "none",
                fontSize: 15,
                fontFamily: SANS,
              }}
            >
              Verify Your Insurance
            </Link>
          </div>
        </div>
      </header>

      {/* TRUST BAR */}
      <div
        style={{
          background: "#FAF4F0",
          borderTop: "3px solid #8B1A1A",
          borderBottom: "1px solid #F0E0E0",
          padding: "16px 0",
        }}
      >
        <div
          style={{
            maxWidth: 1100,
            margin: "0 auto",
            padding: "0 20px",
            display: "grid",
            gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
            gap: 16,
            fontFamily: SANS,
            fontWeight: 700,
            fontSize: 13,
            color: "#2C2C2C",
            textAlign: "center",
          }}
        >
          <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6 }}>
            <MapPin size={16} color="#8B1A1A" /> Accessible from Camden
          </span>
          <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6, borderLeft: "1px solid #F0E0E0" }}>
            <Check size={16} color="#8B1A1A" /> Most Insurance Accepted
          </span>
          <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6, borderLeft: "1px solid #F0E0E0" }}>
            <Shield size={16} color="#8B1A1A" /> Licensed & Accredited
          </span>
          <span style={{ display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 6, borderLeft: "1px solid #F0E0E0" }}>
            <Phone size={16} color="#8B1A1A" /> Available 24/7
          </span>
        </div>
      </div>

      {/* SECTION 2 — LOCAL INTRO */}
      <section style={{ background: "#FFFFFF", padding: "60px 0" }}>
        <div
          style={{
            maxWidth: 1100,
            margin: "0 auto",
            padding: "0 20px",
            display: "grid",
            gridTemplateColumns: "minmax(0, 65fr) minmax(0, 35fr)",
            gap: 40,
          }}
          className="local-intro-grid"
        >
          <div>
            <h2 style={{ fontFamily: SERIF, color: "#8B1A1A", fontSize: 30, margin: 0, lineHeight: 1.2 }}>
              Addiction Treatment Serving Camden and Surrounding Communities
            </h2>
            <p style={{ color: "#2C2C2C", fontSize: 16, lineHeight: 1.75, marginTop: 18, fontFamily: SANS }}>
              Cherry Hill Recovery Center is located at 1930 Marlton Pike East — directly across the border from Camden in Cherry Hill, New Jersey. For Camden City residents and surrounding communities seeking{" "}
              <Link to="/php-cherry-hill-nj" style={{ color: "#8B1A1A" }}>PHP</Link>,{" "}
              <Link to="/iop-cherry-hill-nj" style={{ color: "#8B1A1A" }}>IOP</Link>,{" "}
              <Link to="/medication-assisted-treatment-nj" style={{ color: "#8B1A1A" }}>MAT</Link>, or{" "}
              <Link to="/detox-referral-coordination-nj" style={{ color: "#8B1A1A" }}>detox referral</Link>{" "}
              coordination, our facility is a short drive away and easy to reach by bus. We work with most major insurance plans. That includes Aetna, Cigna, United Healthcare, Blue Cross Blue Shield, Anthem, Optum, Carelon, and Harvard Pilgrim. Our admissions team checks your benefits for free.
            </p>
            <p style={{ color: "#2C2C2C", fontSize: 16, lineHeight: 1.75, marginTop: 14, fontFamily: SANS }}>
              We are the closest accredited outpatient program to Camden — about 7 miles away. Cherry Hill Recovery Center is Joint Commission accredited and serves Camden and the rest of South Jersey.
            </p>

            <h3 style={{ fontFamily: SANS, color: "#6B1414", fontSize: 22, marginTop: 32, fontWeight: 700 }}>
              Addiction and Recovery in Camden, New Jersey
            </h3>
            <p style={{ color: "#2C2C2C", fontSize: 16, lineHeight: 1.75, marginTop: 12, fontFamily: SANS }}>
              Camden is one of the New Jersey communities hit hardest by addiction. Fentanyl now shows up in most street opioids and fake pills, so any use can be deadly. Alcohol problems, stimulant use, and mental health conditions are common here too. We offer the full range of outpatient care — including dual-diagnosis psychiatric treatment — and we check your insurance before you start.
            </p>
            <p style={{ color: "#2C2C2C", fontSize: 16, lineHeight: 1.75, marginTop: 14, fontFamily: SANS }}>
              Unlike many treatment facilities in the Camden area — CHRC offers psychiatric oversight, MAT, and PHP under one roof.
            </p>

            <h3 style={{ fontFamily: SANS, color: "#6B1414", fontSize: 22, marginTop: 32, fontWeight: 700 }}>
              Insurance Coverage for Camden Residents
            </h3>
            <p style={{ color: "#2C2C2C", fontSize: 16, lineHeight: 1.75, marginTop: 12, fontFamily: SANS }}>
              We accept most major insurance plans held by Camden residents. That includes plans through work and plans bought on the marketplace. Under state and federal parity rules, most plans cover addiction treatment such as PHP, IOP, and MAT. Our team checks your coverage for free before you start — call{" "}
              <a href={TEL} style={{ color: "#8B1A1A" }}>{PHONE}</a>{" "}
              to find out what your plan covers.
            </p>

            <h3 style={{ fontFamily: SANS, color: "#6B1414", fontSize: 22, marginTop: 32, fontWeight: 700 }}>
              Camden and Addiction Treatment
            </h3>
            <p style={{ color: "#2C2C2C", fontSize: 16, lineHeight: 1.75, marginTop: 12, fontFamily: SANS }}>
              Camden has faced serious public health problems tied to drug use. Opioid addiction is the biggest one, driven by heroin and fentanyl in the region. If you live in Camden and want structured outpatient care close to home, we are 7 miles away. We offer PHP, IOP, MAT, and detox referral, and we accept most major insurance.
            </p>
          </div>

          {/* SIDEBAR */}
          <aside
            style={{
              position: "sticky",
              top: 90,
              alignSelf: "start",
              background: "#FAF4F0",
              border: "1px solid #F0E0E0",
              borderTop: "4px solid #8B1A1A",
              borderRadius: 12,
              padding: 28,
              fontFamily: SANS,
            }}
          >
            <h3 style={{ color: "#8B1A1A", fontSize: 18, fontWeight: 700, margin: 0 }}>Get Help Today</h3>
            <p style={{ color: "#2C2C2C", fontSize: 14, marginTop: 10, lineHeight: 1.55 }}>
              Free confidential assessment. most insurance accepted. Same-day response.
            </p>
            <a
              href={TEL}
              aria-label={`Call Cherry Hill Recovery Center at ${PHONE}`}
              style={{
                display: "block",
                background: "#8B1A1A",
                color: "#FAF4F0",
                padding: "12px 16px",
                borderRadius: 999,
                fontWeight: 700,
                textDecoration: "none",
                fontSize: 14,
                marginTop: 14,
                textAlign: "center",
              }}
            >
              Call {PHONE}
            </a>

            <hr style={{ border: 0, borderTop: "1px solid #F0E0E0", margin: "16px 0" }} />

            <Link
              to="/verify-insurance"
              aria-label="Verify your insurance coverage"
              style={{
                display: "block",
                background: "transparent",
                color: "#8B1A1A",
                border: "2px solid #8B1A1A",
                padding: "10px 16px",
                borderRadius: 999,
                fontWeight: 700,
                textDecoration: "none",
                fontSize: 14,
                textAlign: "center",
              }}
            >
              Verify My Insurance
            </Link>

            <hr style={{ border: 0, borderTop: "1px solid #F0E0E0", margin: "16px 0" }} />

            <p style={{ color: "#2C2C2C", fontWeight: 700, fontSize: 13, margin: 0 }}>Programs Available:</p>
            <div style={{ marginTop: 8 }}>
              <SidebarLink to="/php-cherry-hill-nj">→ Partial Hospitalization (PHP)</SidebarLink>
              <SidebarLink to="/iop-cherry-hill-nj">→ Intensive Outpatient (IOP)</SidebarLink>
              <SidebarLink to="/virtual-iop-new-jersey">→ Virtual IOP & Telehealth</SidebarLink>
              <SidebarLink to="/medication-assisted-treatment-nj">→ Medication Assisted Treatment</SidebarLink>
              <SidebarLink to="/detox-referral-coordination-nj">→ Detox Referral & Coordination</SidebarLink>
              <SidebarLink to="/psychiatric-evaluation-nj">→ Psychiatric Evaluations</SidebarLink>
            </div>

            <hr style={{ border: 0, borderTop: "1px solid #F0E0E0", margin: "16px 0" }} />

            <p style={{ color: "#2C2C2C", fontWeight: 700, fontSize: 13, margin: 0 }}>Our Location:</p>
            <p style={{ color: "#888", fontSize: 13, marginTop: 6, lineHeight: 1.5 }}>
              1930 Marlton Pike East
              <br />
              Cherry Hill, NJ 08003
            </p>
            <a
              href="https://maps.google.com/maps?daddr=1930+Marlton+Pike+East+Cherry+Hill+NJ+08003"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: "#8B1A1A", fontSize: 13, fontWeight: 700, textDecoration: "none" }}
            >
              Get Directions →
            </a>
          </aside>
        </div>
      </section>

      {/* SECTION 3 — DISTANCE & ACCESS */}
      <section style={{ background: "#FAF4F0", padding: "48px 0" }}>
        <div style={{ maxWidth: 1000, margin: "0 auto", padding: "0 20px" }}>
          <h2
            style={{
              fontFamily: SERIF,
              color: "#8B1A1A",
              fontSize: 28,
              textAlign: "center",
              margin: 0,
              marginBottom: 32,
            }}
          >
            Getting to Cherry Hill Recovery Center from Camden
          </h2>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
              gap: 20,
            }}
          >
            {[
              {
                Icon: Car,
                title: "By Car",
                body:
                  "Cherry Hill Recovery Center is approximately 5 to 10 minutes from Camden City via Route 38 West to Marlton Pike East, or via Haddonfield Road South. Both routes provide direct access from Camden to our Cherry Hill location.",
                small: "1930 Marlton Pike East, Cherry Hill, NJ 08003",
              },
              {
                Icon: Bus,
                title: "By Transit",
                body:
                  "Public transportation options connect Camden to the Cherry Hill area via NJ Transit bus routes. Our admissions team can help Camden patients identify the most accessible transportation route to our facility — call 856-200-3127.",
              },
              {
                Icon: Monitor,
                title: "Virtual Option",
                body:
                  "Our Virtual IOP program delivers the same structured programming via secure telehealth for Camden residents who prefer to attend from home or face transportation barriers.",
              },
            ].map(({ Icon, title, body, small }) => (
              <div
                key={title}
                style={{
                  background: "#FFFFFF",
                  borderRadius: 12,
                  padding: 24,
                  borderTop: "3px solid #8B1A1A",
                  textAlign: "center",
                  boxShadow: "0 6px 18px rgba(139,26,26,0.06)",
                  fontFamily: SANS,
                }}
              >
                <Icon size={24} color="#8B1A1A" />
                <h3 style={{ color: "#2C2C2C", fontSize: 18, fontWeight: 700, marginTop: 10 }}>{title}</h3>
                <p style={{ color: "#555", fontSize: 14, lineHeight: 1.6, marginTop: 8 }}>{body}</p>
                {small && (
                  <p style={{ color: "#888", fontSize: 12, marginTop: 10 }}>{small}</p>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 4 — PROGRAMS */}
      <section style={{ background: "#FFFFFF", padding: "60px 0" }}>
        <div style={{ maxWidth: 1100, margin: "0 auto", padding: "0 20px" }}>
          <h2 style={{ fontFamily: SERIF, color: "#8B1A1A", fontSize: 32, textAlign: "center", margin: 0 }}>
            Addiction Treatment Programs Available to Camden Residents
          </h2>
          <p
            style={{
              color: "#2C2C2C",
              fontSize: 17,
              maxWidth: 700,
              margin: "16px auto 36px",
              textAlign: "center",
              lineHeight: 1.6,
              fontFamily: SANS,
            }}
          >
            Every program is open to patients from Camden and nearby towns. That includes Partial Hospitalization, Intensive Outpatient, Medication-Assisted Treatment, and detox referral. All of it sits about 10 minutes from Camden. We accept most private insurance plans and check your benefits before anything starts.
          </p>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))",
              gap: 22,
            }}
          >
            {[
              {
                Icon: Calendar,
                title: "Partial Hospitalization Program (PHP)",
                body:
                  "Five to six days per week of intensive outpatient treatment. Individual therapy, group counseling, psychiatric evaluation, and medication management. Patients from Camden attend daily and return home each evening.",
                link: "/php-cherry-hill-nj",
                linkText: "Learn About PHP →",
              },
              {
                Icon: Clock,
                title: "Intensive Outpatient Program (IOP)",
                body:
                  "Three to four days per week of structured treatment. Evening and virtual scheduling available for Camden patients who need to maintain work, school, or family responsibilities.",
                link: "/iop-cherry-hill-nj",
                linkText: "Learn About IOP →",
              },
              {
                Icon: Pill,
                title: "Medication Assisted Treatment (MAT)",
                body:
                  "Medication-Assisted Treatment is available to Camden patients as part of PHP or IOP — including Suboxone and Sublocade for opioid use disorder under the oversight of Dr. Jeffrey Simon. Never provided as a standalone prescription.",
                link: "/medication-assisted-treatment-nj",
                linkText: "Learn About MAT →",
              },
              {
                Icon: Network,
                title: "Detox Referral & Coordination",
                body:
                  "We assess each patient's needs and refer them to the right medically supervised detox provider — coordinating the process and transitioning patients directly into PHP or IOP upon completion.",
                link: "/detox-referral-coordination-nj",
                linkText: "Learn About Detox Referral →",
              },
            ].map(({ Icon, title, body, link, linkText }) => (
              <div
                key={title}
                style={{
                  background: "#FAF4F0",
                  borderRadius: 12,
                  padding: 28,
                  borderTop: "4px solid #8B1A1A",
                  boxShadow: "0 6px 18px rgba(107,20,20,0.08)",
                  fontFamily: SANS,
                }}
              >
                <Icon size={26} color="#8B1A1A" />
                <h3 style={{ color: "#2C2C2C", fontSize: 19, fontWeight: 700, marginTop: 10 }}>{title}</h3>
                <p style={{ color: "#2C2C2C", fontSize: 15, lineHeight: 1.65, marginTop: 10 }}>{body}</p>
                <Link
                  to={link}
                  style={{
                    display: "inline-block",
                    color: "#8B1A1A",
                    fontWeight: 700,
                    fontSize: 14,
                    textDecoration: "none",
                    marginTop: 12,
                  }}
                >
                  {linkText}
                </Link>
              </div>
            ))}
          </div>

          <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "center", marginTop: 28 }}>
            {programs.map((p) => (
              <PillLink key={p.href} to={p.href}>
                {p.label}
              </PillLink>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 5 — CONDITIONS */}
      <section style={{ background: "#FAF4F0", padding: "60px 0" }}>
        <div style={{ maxWidth: 1100, margin: "0 auto", padding: "0 20px" }}>
          <h2 style={{ fontFamily: SERIF, color: "#8B1A1A", fontSize: 30, textAlign: "center", margin: 0 }}>
            Conditions We Treat
          </h2>
          <p
            style={{
              color: "#2C2C2C",
              fontSize: 16,
              textAlign: "center",
              maxWidth: 760,
              margin: "14px auto 28px",
              lineHeight: 1.6,
              fontFamily: SANS,
            }}
          >
            Our clinical team treats the full range of substance use disorders alongside co-occurring mental health conditions — serving patients from Camden, Cherry Hill, and all of South Jersey.
          </p>
          <p
            style={{
              color: "#2C2C2C",
              fontSize: 16,
              textAlign: "center",
              maxWidth: 760,
              margin: "0 auto 28px",
              lineHeight: 1.6,
              fontFamily: SANS,
            }}
          >
            Camden County has been significantly impacted by the opioid crisis — opioid addiction including heroin and fentanyl use disorder is one of the most common presentations among patients we treat from the Camden area.
          </p>
          <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "center" }}>
            {conditions.map((c) => (
              <PillLink key={c.href + c.label} to={c.href}>
                {c.label}
              </PillLink>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 6 — LOCAL STATS */}
      <section style={{ background: "#FFFFFF", padding: "60px 0" }}>
        <div style={{ maxWidth: 1100, margin: "0 auto", padding: "0 20px" }}>
          <h2 style={{ fontFamily: SERIF, color: "#8B1A1A", fontSize: 30, margin: 0 }}>
            Addiction in Camden — Why Local Treatment Matters
          </h2>
          <p style={{ color: "#2C2C2C", fontSize: 16, lineHeight: 1.75, marginTop: 16, fontFamily: SANS }}>
            New Jersey is one of the states hit hardest by addiction, and Camden feels it more than most. Fentanyl is now in most of the opioid supply across South Jersey. Heavy drinking is still common. Most people who need help are not in a residential program. They are working, raising kids, and living in Camden. They need care that fits their life.
          </p>
          <p style={{ color: "#2C2C2C", fontSize: 16, lineHeight: 1.75, marginTop: 14, fontFamily: SANS }}>
            Methadone clinics require a visit every day. Our MAT program uses Suboxone and Sublocade instead. Patients come three to five days a week and live at home in Camden.
          </p>
          <p style={{ color: "#2C2C2C", fontSize: 16, lineHeight: 1.75, marginTop: 14, fontFamily: SANS }}>
            Outpatient care is not a lesser option. For most people with a substance use problem, PHP or IOP is the right level of care. It works, most insurance covers it, and it is what we do every day for Camden residents and families across South Jersey.
          </p>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
              gap: 20,
              marginTop: 32,
            }}
          >
            {[
              { num: "#1", label: "Fentanyl — leading cause of overdose death in New Jersey" },
              { num: "Most", label: "People with addiction need outpatient — not residential — treatment" },
              { num: "24/7", label: "Cherry Hill Recovery Center admissions team availability" },
            ].map((s) => (
              <div
                key={s.num + s.label}
                style={{
                  background: "#FAF4F0",
                  borderRadius: 12,
                  padding: 24,
                  borderTop: "3px solid #8B1A1A",
                  textAlign: "center",
                  fontFamily: SANS,
                }}
              >
                <div style={{ color: "#8B1A1A", fontWeight: 700, fontSize: 32 }}>{s.num}</div>
                <div style={{ color: "#555", fontSize: 13, marginTop: 8, lineHeight: 1.5 }}>{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 7 — INSURANCE */}
      <section style={{ background: "#FAF4F0", padding: "60px 0" }}>
        <div style={{ maxWidth: 1100, margin: "0 auto", padding: "0 20px", textAlign: "center" }}>
          <h2 style={{ fontFamily: SERIF, color: "#8B1A1A", fontSize: 30, margin: 0 }}>
            Insurance Coverage for Camden Residents
          </h2>
          <p
            style={{
              color: "#2C2C2C",
              fontSize: 16,
              maxWidth: 680,
              margin: "16px auto 28px",
              lineHeight: 1.7,
              fontFamily: SANS,
            }}
          >
            We accept most major insurance plans held by Camden residents. Under the Mental Health Parity Act, most plans must cover addiction treatment. Our team checks your benefits for free before you start.
          </p>
          <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "center", marginBottom: 32 }}>
            <InsurancePills insurers={insurers} style={{ justifyContent: "center" }} />
          </div>
          <Link
            to="/verify-insurance"
            aria-label="Verify your insurance — free"
            style={{
              display: "inline-block",
              background: "#8B1A1A",
              color: "#FAF4F0",
              padding: "14px 36px",
              borderRadius: 999,
              fontWeight: 700,
              textDecoration: "none",
              fontSize: 15,
              fontFamily: SANS,
            }}
          >
            Verify My Insurance — Free
          </Link>
        </div>
      </section>

      {/* SECTION 8 — NEARBY AREAS */}
      <section style={{ background: "#FFFFFF", padding: "48px 0" }}>
        <div style={{ maxWidth: 1000, margin: "0 auto", padding: "0 20px", textAlign: "center" }}>
          <h2 style={{ fontFamily: SERIF, color: "#8B1A1A", fontSize: 26, margin: 0 }}>
            Other Communities We Serve Near Camden
          </h2>
          <p style={{ color: "#555", fontSize: 15, marginTop: 14, marginBottom: 20, fontFamily: SANS }}>
            Cherry Hill Recovery Center serves patients from throughout South Jersey and the greater Philadelphia region.
          </p>
          <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "center" }}>
            {nearby.map((n) => (
              <PillLink key={n.href + n.label} to={n.href}>
                {n.label}
              </PillLink>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 9 — MAP */}
      <section style={{ background: "#FAF4F0", padding: "48px 0" }}>
        <div style={{ maxWidth: 1000, margin: "0 auto", padding: "0 20px", textAlign: "center" }}>
          <h2 style={{ fontFamily: SERIF, color: "#8B1A1A", fontSize: 24, margin: 0, marginBottom: 20 }}>
            Getting to Cherry Hill Recovery Center from Camden
          </h2>
          <LazyMap src="https://www.google.com/maps?q=1930+Marlton+Pike+East,+Cherry+Hill,+NJ+08003&z=13&output=embed" title="Cherry Hill Recovery Center — serving Camden NJ" height={340} />
          <div style={{ marginTop: 16, fontFamily: SANS }}>
            <div style={{ fontWeight: 700, fontSize: 14, color: "#2C2C2C" }}>Cherry Hill Recovery Center</div>
            <div style={{ fontSize: 13, color: "#555", marginTop: 4 }}>
              1930 Marlton Pike East, Cherry Hill, NJ 08003
            </div>
            <a
              href="https://maps.google.com/maps?saddr=Camden+NJ&daddr=1930+Marlton+Pike+East+Cherry+Hill+NJ+08003"
              target="_blank"
              rel="noopener noreferrer"
              style={{ color: "#8B1A1A", fontSize: 13, fontWeight: 700, textDecoration: "none", display: "inline-block", marginTop: 10 }}
            >
              Get Directions from Camden →
            </a>
          </div>
        </div>
      </section>

      {/* SECTION 10 — FAQ */}
      <section style={{ background: "#FFFFFF", padding: "60px 0" }}>
        <div style={{ maxWidth: 900, margin: "0 auto", padding: "0 20px" }}>
          <h2 style={{ fontFamily: SERIF, color: "#8B1A1A", fontSize: 30, margin: 0, marginBottom: 24 }}>
            Frequently Asked Questions — Addiction Treatment Near Camden, NJ
          </h2>
          <Accordion type="single" collapsible className="space-y-3">
            {faqs.map((f, i) => (
              <AccordionItem
                key={i}
                value={`item-${i}`}
                style={{
                  background: "#FFFFFF",
                  border: 0,
                  borderBottom: "1px solid #F0E0E0",
                  borderRadius: 0,
                  paddingLeft: 8,
                  paddingRight: 8,
                }}
              >
                <AccordionTrigger className="hover:no-underline py-5 text-left">
                  <h3
                    style={{
                      color: "#2C2C2C",
                      fontSize: 16,
                      fontWeight: 600,
                      margin: 0,
                      fontFamily: SANS,
                    }}
                  >
                    {f.q}
                  </h3>
                </AccordionTrigger>
                <AccordionContent
                  style={{
                    background: "#FAF4F0",
                    color: "#2C2C2C",
                    fontSize: 15,
                    lineHeight: 1.7,
                    padding: "16px 20px",
                    borderRadius: 8,
                    fontFamily: SANS,
                  }}
                >
                  {f.a}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* SECTION 11 — BOTTOM CTA */}
      <section
        style={{
          background: "linear-gradient(135deg, #6B1414 0%, #8B1A1A 100%)",
          padding: "64px 20px",
        }}
      >
        <div style={{ maxWidth: 760, margin: "0 auto", textAlign: "center" }}>
          <h2 style={{ fontFamily: SERIF, fontSize: 36, color: "#FFFFFF", margin: 0, lineHeight: 1.2 }}>
            Addiction Treatment Accessible From Camden — Available Now.
          </h2>
          <p
            style={{
              color: "#FAF4F0",
              fontSize: 18,
              maxWidth: 600,
              margin: "16px auto 0",
              lineHeight: 1.55,
              fontFamily: SANS,
            }}
          >
            PHP, IOP, MAT, and detox referral from Cherry Hill Recovery Center. most insurance accepted. Free assessment, same-day response.
          </p>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 12, justifyContent: "center", marginTop: 24 }}>
            <a
              href={TEL}
              aria-label={`Call Cherry Hill Recovery Center at ${PHONE}`}
              style={{
                background: "#FAF4F0",
                color: "#8B1A1A",
                padding: "12px 26px",
                borderRadius: 999,
                fontWeight: 700,
                textDecoration: "none",
                fontSize: 15,
                fontFamily: SANS,
              }}
            >
              Call {PHONE}
            </a>
            <Link
              to="/verify-insurance"
              aria-label="Verify your insurance coverage"
              style={{
                background: "transparent",
                color: "#FAF4F0",
                border: "2px solid #FAF4F0",
                padding: "10px 24px",
                borderRadius: 999,
                fontWeight: 700,
                textDecoration: "none",
                fontSize: 15,
                fontFamily: SANS,
              }}
            >
              Verify Your Insurance
            </Link>
          </div>
          <div
            style={{
              display: "flex",
              flexWrap: "wrap",
              justifyContent: "center",
              gap: 18,
              marginTop: 24,
              color: "rgba(250,200,200,0.9)",
              fontSize: 13,
              fontFamily: SANS,
            }}
          >
            <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
              <Lock size={14} /> 100% Confidential
            </span>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
              <Check size={14} /> Insurance Accepted
            </span>
            <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
              <Phone size={14} /> Same-Day Response
            </span>
          </div>
        </div>
      </section>

      <LocalContextSection
        heading="What treatment looks like for Camden residents"
        intro={<>Camden is minutes from our Cherry Hill office, and it carries some of the heaviest overdose numbers in New Jersey. The people we see from Camden are often dealing with fentanyl-contaminated supply, transportation barriers, and prior treatment attempts that ended when the funding or the ride ran out.</>}
        notes={[
          {
            title: "Fentanyl is the default",
            body: <>Almost nothing sold as heroin in Camden is only heroin. That changes withdrawal, overdose risk, and how medication is dosed. See <LocalLink to="/fentanyl-addiction-treatment-nj">fentanyl treatment</LocalLink> and <LocalLink to="/heroin-opioid-addiction-treatment-nj">opioid treatment</LocalLink>.</>,
          },
          {
            title: "Beyond the daily clinic trip",
            body: <>Many Camden residents are already tied to a daily methadone window. We add the therapy, psychiatric care, and structure around the medication. Read about <LocalLink to="/methadone-clinics-camden-county-nj">methadone clinics in Camden County</LocalLink>.</>,
          },
          {
            title: "Transportation planned up front",
            body: <>Missed rides end more treatment episodes than missed motivation. We sort out transit, scheduling, or <LocalLink to="/virtual-iop-new-jersey">virtual IOP</LocalLink> during intake rather than after the first no-show.</>,
          },
          {
            title: "Stimulants in the mix",
            body: <>Camden sees a lot of combined opioid and stimulant use. Medication handles one side; <LocalLink to="/cocaine-addiction-treatment-nj">stimulant-focused therapy</LocalLink> handles the other.</>,
          },
        ]}
        gettingHereHeading="Travel and scheduling logistics for Camden patients"
        gettingHere={[
          <>Admiral Wilson Boulevard (Route 30) east to Route 70 — about 10 to 15 minutes by car.</>,
          <>NJ Transit bus service runs from the Walter Rand Transportation Center toward Cherry Hill.</>,
          <>PATCO from Ferry Avenue or Broadway connects to Haddonfield and Westmont, both a short ride from our office.</>,
          <>Free on-site parking, and same-day assessments when a bed or a slot is time-sensitive.</>,
        ]}
        closing={<>Keep naloxone (Narcan) on hand — it is available without a prescription at New Jersey pharmacies. Then call us and we will start the assessment the same day.</>}
      />

      <LocalCoverageSection
        town="Camden"
        county="Camden County"
        neighborhoods={["Cooper Grant", "Lanning Square", "Fairview", "Parkside", "Cramer Hill", "East Camden", "Waterfront South", "Centerville", "Whitman Park", "Rosedale", "Stockton", "Marlton neighborhood"]}
        zips={["08102", "08103", "08104", "08105"]}
        neighborhoodNote={<>Camden is a city of distinct neighborhoods, and access to treatment looks different in each one. We see patients from the Cooper Grant and Lanning Square blocks near the hospital campus, from Parkside and Whitman Park, from Cramer Hill and East Camden across the Admiral Wilson corridor, and from Fairview down in the south end. Transit access, not distance, is usually the deciding factor — our Cherry Hill office is about 15 minutes east by car and directly reachable by bus.</>}
        resources={[
          { name: "Camden County Office of Mental Health and Addiction", detail: "county-level coordination for residents who need help navigating coverage, including those on NJ FamilyCare or without insurance." },
          { name: "Cooper University Health Care — Center for Healing", detail: "a Camden-based provider of medication for opioid use disorder and harm reduction services. Many of our Camden referrals come through or return to Cooper for medical care." },
          { name: "Camden Coalition of Healthcare Providers", detail: "long-standing Camden organization focused on care coordination for residents with complex medical and social needs." },
          { name: "NJ Reach (1-844-276-2777)", detail: "24/7 statewide addiction help line, staffed at all hours and able to route Camden residents to county services." },
          { name: "Camden County Needle Exchange and harm reduction services", detail: "sterile supplies, naloxone, and testing — a pragmatic first contact point for people not yet ready for treatment." },
        ]}
        referralBody={<>Camden residents often reach us after an emergency department visit at Cooper University Hospital or Virtua Our Lady of Lourdes. If detox is needed first, we assess you and coordinate placement with a licensed South Jersey detox provider rather than sending you to figure it out alone — then bring you into <LocalLink to="/php-cherry-hill-nj">PHP</LocalLink> or <LocalLink to="/iop-cherry-hill-nj">IOP</LocalLink> once you are cleared. For opioid use disorder specifically, medication can start early: see <LocalLink to="/medication-assisted-treatment-nj">medication-assisted treatment</LocalLink> and our <LocalLink to="/suboxone-doctor-cherry-hill-nj">Suboxone program</LocalLink>. If cost or coverage is the obstacle, call anyway — verification takes a few minutes and costs nothing. Details on <LocalLink to="/detox-referral-coordination-nj">detox referral coordination</LocalLink>.</>}
      />

      <RelatedLinks exclude={["/addiction-treatment-camden-nj"]} towns={["Cherry Hill", "Pennsauken", "Haddonfield", "Voorhees"]} />

      <Footer />
      <FloatingCTAs />

      <style>{`
        @media (max-width: 900px) {
          .local-intro-grid { grid-template-columns: 1fr !important; }
          .local-intro-grid aside { position: static !important; }
        }
      `}</style>
    </div>
  );
};

export default AddictionTreatmentCamdenNJ;
